import React, {createContext, ReactNode, useContext, useMemo} from 'react'
import {GameEndData, VictoryPointStateReader} from '@colonist/game-base-types'
import {EndGamePlayerState, PlayerColor} from '@colonist/game-states'
import {BaseRankedUserState, RankedUserStateController} from '@colonist/leaderboard-types'
import {useGameStore} from '../store/GameStoreContext'

interface UIGameEndContextProps {
    onClickRematch: () => void
    onClickPlayAgain: () => void
    onClickRetryBeginnerMode: () => void
    onClickNextLevelBeginnerMode: () => void
    onClickHome: () => void
    onClickShare: () => void
    onClickStatsOrReplay: () => void
    setShowEndGameScreen: (visible: boolean) => void
    isMyColorWinner: boolean
    numberOfPlayersJoinedRematch: number
    playersByRank: (EndGamePlayerState & { totalVictoryPoints: number })[]
    gameEndData: GameEndData
    currentPlayerEndgameState: EndGamePlayerState | undefined
    currentPlayerRankedState: BaseRankedUserState | undefined
    userRankedStatesByColor: {[key in PlayerColor]?: BaseRankedUserState}
    isPlayerHasRankedState: boolean
}

export const UIGameEndContext = createContext<UIGameEndContextProps | undefined>(undefined)

export const useUIGameEndContext = () => {
    const context = useContext(UIGameEndContext)
    if(!context) throw new Error('UIGameEndContext not found')
    return context
}

export interface UIGameEndProviderProps {
    onClickRematch: () => void
    onClickPlayAgain: () => void
    onClickRetryBeginnerMode: () => void
    onClickNextLevelBeginnerMode: () => void
    onClickHome: () => void
    onClickShare: () => void
    onClickStatsOrReplay: () => void
    onEndGameScreenOpen: () => void
    onEndGameScreenClose: () => void
    gameEndData: GameEndData
    numberOfPlayersJoinedRematch: number
    children: ReactNode
}

export function UIGameEndProvider({
    onClickRematch,
    onClickPlayAgain,
    onClickRetryBeginnerMode,
    onClickNextLevelBeginnerMode,
    onClickHome,
    onClickShare,
    onClickStatsOrReplay,
    onEndGameScreenOpen,
    onEndGameScreenClose,
    gameEndData,
    numberOfPlayersJoinedRematch,
    children,
}: UIGameEndProviderProps) {
    const myColor = useGameStore((state) => state.myColor)
    const isMyColorWinner = useMemo(() => gameEndData.endGameState.players[myColor]?.winningPlayer === true, [gameEndData, myColor])

    const setShowEndGameScreen = (visible: boolean) => {
        if(visible) onEndGameScreenOpen()
        else onEndGameScreenClose()
    }

    const playersByRank = useMemo(() => Object.values(gameEndData.endGameState.players)
        .sort((a, b) => a.rank - b.rank)
        .map((item) => ({
            ...item,
            totalVictoryPoints: VictoryPointStateReader.totalPrivateVictoryPoints(item.victoryPoints),
        })), [gameEndData])

    const currentPlayerEndgameState = gameEndData?.endGameState.players[myColor]

    const {currentPlayerRankedState, userRankedStatesByColor} = useMemo(() => {
        const userRankedStatesByColorMap: {[key in PlayerColor]?: BaseRankedUserState} = {}
        gameEndData?.rankedUserStates?.forEach((rankedUserState) => {
            const deserializedRankedUserState = RankedUserStateController.deserialize(rankedUserState.rankedState)
            userRankedStatesByColorMap[rankedUserState.color] = deserializedRankedUserState
        })
        return {
            currentPlayerRankedState: userRankedStatesByColorMap[myColor],
            userRankedStatesByColor: userRankedStatesByColorMap,
        }
    }, [gameEndData, myColor])

    const isPlayerHasRankedState = Boolean(currentPlayerRankedState)

    return (
        <UIGameEndContext.Provider
            value={{
                onClickRematch: onClickRematch,
                onClickPlayAgain: onClickPlayAgain,
                onClickRetryBeginnerMode: onClickRetryBeginnerMode,
                onClickNextLevelBeginnerMode: onClickNextLevelBeginnerMode,
                onClickHome: onClickHome,
                onClickShare: onClickShare,
                onClickStatsOrReplay: onClickStatsOrReplay,
                setShowEndGameScreen: setShowEndGameScreen,
                isMyColorWinner: isMyColorWinner,
                numberOfPlayersJoinedRematch: numberOfPlayersJoinedRematch,
                playersByRank: playersByRank,
                gameEndData: gameEndData,
                currentPlayerEndgameState: currentPlayerEndgameState,
                currentPlayerRankedState: currentPlayerRankedState,
                userRankedStatesByColor: userRankedStatesByColor,
                isPlayerHasRankedState: isPlayerHasRankedState,
            }}
        >
            {children}
        </UIGameEndContext.Provider>
    )
}
